import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { AnimatePresence, motion, usePresence, useReducedMotion } from 'motion/react';
import { curves, seconds } from '../theme/motion';
import { useStatusBarBarrier } from '../theme/themeMode';
import { Button } from './Button';
import { Portal } from './Portal';
import { useFocusTrap } from './useFocusTrap';
import { useKeyboardInset } from './useKeyboardInset';
import { useOverlayHistory } from './useOverlayHistory';
import { useTopLayer } from './useTopLayer';
import styles from './Dialog.module.css';

export interface DialogActionSpec {
  label: string;
  onClick: () => void;
  /** `destructive` = error colours; `primary` (default) = accent. */
  tone?: 'primary' | 'destructive';
  /** Material: TextButton (default) or FilledButton. Ignored by the Cupertino variant. */
  variant?: 'text' | 'filled';
  /** Cupertino: bold default action. */
  isDefault?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}

export interface DialogProps {
  open: boolean;
  /** Barrier tap, Escape or Back. */
  onClose: () => void;
  /** After the exit fade. */
  onClosed?: () => void;
  title?: ReactNode;
  /** `headline` = M3 headlineSmall (Merriweather 17/700, default); `title` = Inter 16/700 (downloads dialogs). */
  titleStyle?: 'headline' | 'title';
  children?: ReactNode;
  /** Buttons, right-aligned with 8 px spacing (or custom nodes). */
  actions?: DialogActionSpec[] | ReactNode;
  /** `material` AlertDialog (24 px corners) or iOS-style `cupertino` alert. */
  variant?: 'material' | 'cupertino';
  dismissible?: boolean;
  historyDismiss?: boolean;
  /** `role="alertdialog"` for confirmations that interrupt the user (confirmDialog); default `dialog`. */
  alert?: boolean;
  initialFocus?: RefObject<HTMLElement | null>;
  'aria-label'?: string;
  className?: string;
  contentClassName?: string;
}

/**
 * Material 3 AlertDialog with Plume's theme: surface background, radius 24,
 * black54 barrier, 150 ms fade (spec 01 §4.4). Polish pass: all four corners
 * are rounded (Flutter reused the sheet's top-only radius), the layer centres
 * the box above the on-screen keyboard and clear of the safe areas, and the
 * status bar dims with the barrier.
 */
export function Dialog(props: DialogProps) {
  return (
    <Portal>
      <AnimatePresence onExitComplete={props.onClosed}>{props.open && <DialogLayer key="dialog" {...props} />}</AnimatePresence>
    </Portal>
  );
}

function isActionList(actions: DialogProps['actions']): actions is DialogActionSpec[] {
  return Array.isArray(actions) && actions.every((a) => typeof a === 'object' && a !== null && 'label' in a && 'onClick' in a);
}

function DialogLayer({
  onClose,
  title,
  titleStyle = 'headline',
  children,
  actions,
  variant = 'material',
  dismissible = true,
  historyDismiss = true,
  alert = false,
  initialFocus,
  'aria-label': ariaLabel,
  className,
  contentClassName,
}: DialogProps) {
  const [isPresent, safeToRemove] = usePresence();
  const reduceMotion = useReducedMotion() ?? false;
  const boxRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const contentId = useId();

  useOverlayHistory(isPresent, onClose, historyDismiss);
  useTopLayer(isPresent, dismissible ? onClose : undefined);
  useFocusTrap(boxRef, isPresent, { initialFocus });
  // Subscribing publishes `--keyboard-inset`, which the layer pads its bottom by.
  useKeyboardInset();
  const reportBarrier = useStatusBarBarrier();

  useEffect(() => {
    if (!isPresent) {
      const timer = setTimeout(() => safeToRemove?.(), reduceMotion ? 0 : seconds.dialog * 1000);
      return () => clearTimeout(timer);
    }
  }, [isPresent, reduceMotion, safeToRemove]);

  const fade = { duration: reduceMotion ? 0 : seconds.dialog, ease: curves.easeOut };
  const cupertino = variant === 'cupertino';

  return (
    <div className={styles.layer} style={{ pointerEvents: isPresent ? undefined : 'none' }}>
      <motion.div
        className={styles.barrier}
        initial={{ opacity: 0 }}
        animate={{ opacity: isPresent ? 1 : 0 }}
        transition={fade}
        onUpdate={(latest) => reportBarrier(Number(latest.opacity))}
        onClick={dismissible ? onClose : undefined}
        aria-hidden
      />
      <motion.div
        ref={boxRef}
        role={alert ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-label={title ? undefined : ariaLabel}
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={children ? contentId : undefined}
        className={[styles.box, cupertino ? styles.cupertino : styles.material, className ?? ''].filter(Boolean).join(' ')}
        initial={{ opacity: 0, scale: cupertino ? 1.1 : 1 }}
        animate={{ opacity: isPresent ? 1 : 0, scale: 1 }}
        transition={fade}
      >
        <div className={styles.scroll}>
          {title != null && (
            <h2 id={titleId} className={[styles.title, styles[`title-${titleStyle}`], children != null ? styles.titleSpaced : ''].filter(Boolean).join(' ')}>
              {title}
            </h2>
          )}
          {children != null && (
            <div id={contentId} className={[styles.content, contentClassName ?? ''].filter(Boolean).join(' ')}>
              {children}
            </div>
          )}
        </div>
        {actions != null &&
          (isActionList(actions) ? (
            cupertino ? (
              <CupertinoActions actions={actions} />
            ) : (
              <DialogActions>
                {actions.map((action) => (
                  <Button
                    key={action.label}
                    variant={action.variant === 'filled' ? 'filled' : 'text'}
                    tone={action.tone === 'destructive' ? 'error' : 'primary'}
                    disabled={action.disabled}
                    autoFocus={action.autoFocus}
                    onClick={action.onClick}
                  >
                    {action.label}
                  </Button>
                ))}
              </DialogActions>
            )
          ) : (
            <DialogActions>{actions}</DialogActions>
          ))}
      </motion.div>
    </div>
  );
}

/** Right-aligned action row (M3 actions padding 24, spacing 8). */
export function DialogActions({ children }: { children: ReactNode }) {
  return <div className={styles.actions}>{children}</div>;
}

function CupertinoActions({ actions }: { actions: DialogActionSpec[] }) {
  const stacked = actions.length > 2;
  return (
    <div className={[styles.cupertinoActions, stacked ? styles.cupertinoStacked : ''].filter(Boolean).join(' ')}>
      {actions.map((action) => (
        <button
          key={action.label}
          type="button"
          className={[
            styles.cupertinoAction,
            action.tone === 'destructive' ? styles.cupertinoDestructive : '',
            action.isDefault ? styles.cupertinoDefault : '',
          ]
            .filter(Boolean)
            .join(' ')}
          disabled={action.disabled}
          autoFocus={action.autoFocus}
          onClick={action.onClick}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}
